import * as React from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ValidateInvitationTokenStep from './_validateInvitationTokenFormItems';
import { OrganisationAssignmentDto } from 'models/dtoTypes';
import SignupStep from './_signupStep';
import { useState } from 'react';
import { SmeSignup, ValidateSMEInvitationToken } from 'services/smeUser';
import axios from 'axios';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';

interface IProps {
  login?: () => void;
}

export default function SignupStepper(props: IProps) {
  const { t } = useTranslation('default');

  const [tokenError, setTokenError] = React.useState('');
  const [signupError, setSignupError] = React.useState('');

  const [organisatonAssignment, setOrganisationAssignment] = useState<OrganisationAssignmentDto>();
  const [activeStep, setActiveStep] = React.useState(0);

  const handleValidateInvitationToken = async (token: string) => {
    try {
      const res = await ValidateSMEInvitationToken(token);
      setOrganisationAssignment(res);
      setTokenError('');
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } catch (e) {
      console.error(e);
      if (axios.isAxiosError(e) && e.response) {
        const errorMessage = get(e.response?.data, 'message') || 'Something went wrong';
        setTokenError(errorMessage);
      } else {
        setTokenError(e instanceof Error ? e.message : 'Network error');
      }
    }
  };

  const handleSignup = async (email: string, pass: string): Promise<void> => {
    if (!organisatonAssignment) {
      setSignupError('Invitation token is missing');
      return;
    }
    try {
      await SmeSignup({
        email,
        password: pass,
        organisationAssignmentId: organisatonAssignment.id,
      });
      setSignupError('');
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } catch (e) {
      console.error(e);
      if (axios.isAxiosError(e) && e.response) {
        const errorMessage = get(e.response?.data, 'message') || 'Something went wrong';
        setSignupError(errorMessage);
      } else {
        setSignupError(e instanceof Error ? e.message : 'Network error');
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const renderLastStep = () => {
    return (
      <React.Fragment>
        <Typography sx={{ mt: 2, mb: 1 }}>
          All steps completed -
          <Button variant="text" color="secondary" onClick={props.login}>
            {t('login')}
          </Button>
        </Typography>
      </React.Fragment>
    );
  };

  const renderValidateInvitationTokenPanel = () => {
    return (
      <ValidateInvitationTokenStep
        onValidate={handleValidateInvitationToken}
        defaultToken={organisatonAssignment?.id}
      />
    );
  };

  const renderSignupForm = () => {
    return (
      <SignupStep
        onSignup={handleSignup}
        onBack={handleBack}
        companyName={organisatonAssignment?.kosmosCompanyName || ''}
      />
    );
  };

  return (
    <Box sx={{ width: '100%', alignItems: 'center' }}>
      <Stepper activeStep={activeStep}>
        <Step>
          <StepLabel
            error={Boolean(tokenError)}
            optional={
              <Typography variant="caption" color="error">
                {tokenError}
              </Typography>
            }
          >
            Invitation Token
          </StepLabel>
        </Step>
        <Step>
          <StepLabel
            error={Boolean(signupError)}
            optional={
              <Typography variant="caption" color="error">
                {signupError}
              </Typography>
            }
          >
            Signup
          </StepLabel>
        </Step>
        <Step>
          <StepLabel>Go To Portal</StepLabel>
        </Step>
      </Stepper>
      <Box gap={4} p={2}>
        {activeStep === 0 && renderValidateInvitationTokenPanel()}
        {activeStep === 1 && renderSignupForm()}
        {activeStep === 2 && renderLastStep()}
      </Box>
    </Box>
  );
}
